// File: src/pages/targets/HeadtargetingList.jsx
import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { IoEye } from "react-icons/io5";
import { FaFilter, FaPlus } from "react-icons/fa";
import Multiselect from "multiselect-react-dropdown";

// ---- project imports (as per your folder rules) ----
import UserContext from "../import/context/Context";
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import { _Get, _Post } from "../import/ApiEndPoint/ApiCalling";
import {
  Heirarchy_Created_Target_List,
  Create_Account_List,
  MonthMetrics,
} from "../import/ApiEndPoint/Api";

import TargetAssignedOne from "./TargetAssingnedOne";
import TargetCreation from "./TargetCreation";

import {
  exportDataToPDF,
  exportDataToExcel,
  convertDataCSVtoExcel,
  convertDataCsvToXml,
} from "../import/Downloader";

// ---------- tiny, page-scoped CSS ----------
const injectOnce = () => {
  if (document.getElementById("targets-inline-css")) return;
  const style = document.createElement("style");
  style.id = "targets-inline-css";
  style.innerHTML = `
    .tg-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .tg-card{ border:1px solid #e5e7eb; border-radius:14px; }
    .tg-card-light{ border:1px solid #e5e7eb; border-radius:14px; background:#f4f7f6; }

    .tg-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .tg-table{ width:100%; border-collapse:collapse; min-width:1080px; }
    .tg-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .tg-table tbody td{ padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; text-align:center; font-size:14px; }

    .tg-actions{ display:flex; gap:10px; justify-content:center; align-items:center; }

    .pick-box{
      border:1px solid #e5e7eb; border-radius:10px; max-height:280px; overflow:auto; background:#fff; padding:8px;
    }
    .pick-item{
      padding:8px 10px; border:1px solid #e5e7eb; border-radius:8px; margin-bottom:6px; background:#fff; cursor:pointer;
    }

    .dl-menu{
      position:absolute; right:0; top:32px; z-index:10; border:1px solid rgb(8, 91, 245); background:#fff; border-radius:10px;
      overflow:hidden; min-width:140px; box-shadow:0 12px 24px rgba(0,0,0,.12);
    }
    .dl-item{ padding:10px 12px; cursor:pointer; font-weight:600; letter-spacing:.02em; }
    .dl-item:hover{ background:#f4f7f6; }

    .muted{ color:#64748b; }
    .chips{ display:flex; gap:8px; flex-wrap:wrap; }
    .chip{ background:#eef2ff; color:#3730a3; border-radius:999px; padding:4px 10px; font-weight:600; }
  `;
  document.head.appendChild(style);
};

// helpers
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const toNum = (v) => (isFinite(+v) ? +v : 0);
const fmtDate = (s) => (s ? String(s).split("T")[0] : "");

// Year/Month helpers
const NOW = new Date();
const CURRENT_YEAR = NOW.getFullYear();
const YEARS = Array.from({ length: 8 }, (_, i) => CURRENT_YEAR - 5 + i);
const MONTHS = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

export default function HeadtargetingList() {
  const { id } = useParams(); // ← page expects an id in route param (kept as requested)
  const navigate = useNavigate();
  const ctx = useContext(UserContext);
  useEffect(() => injectOnce(), []);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Download: true,
  });

  const [masterShow, setMasterShow] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  const [rows, setRows] = useState([]);
  const [sumTotal, setSumTotal] = useState(0);
  const [search, setSearch] = useState("");

  // “party target” shortcut (your original partyId branch) → show TargetCreation
  const [partyView, setPartyView] = useState(false);
  const [partyData, setPartyData] = useState([]);

  // view details modal (TargetAssignedOne)
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  // column chooser
  const [modalCols, setModalCols] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  // ---------- Sales Person, Year, Month & MonthMetrics ----------
  const [salesPeople, setSalesPeople] = useState([]); // [{_id, sId, fullName, ...}]
  const [selectedSalesId, setSelectedSalesId] = useState(""); // store _id (MonthMetrics expects _id array)
  const [selectedSalesObj, setSelectedSalesObj] = useState(null);

  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [selectedMonth, setSelectedMonth] = useState(NOW.getMonth() + 1);

  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsRows, setMetricsRows] = useState([]); // from MonthMetrics

  const userData = useMemo(
    () => JSON.parse(localStorage.getItem("userData") || "{}"),
    [],
  );
  const db = userData?.database;
  const uid = userData?._id;

  const loadSalesPeople = async () => {
    try {
      const url = `${Create_Account_List}${uid}/`;
      const usersRes = await _Get(url, db);
      const onlySales =
        (usersRes?.adminDetails || []).filter(
          (x) => x?.rolename?.roleName === "Sales Person",
        ) || [];

      const shaped = onlySales.map((p) => ({
        ...p,
        id: p?._id,
        fullName:
          `${p?.firstName || ""} ${p?.lastName || ""}`.trim() ||
          p?.fullName ||
          p?.email ||
          "Sales Person",
      }));
      setSalesPeople(shaped);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMonthMetrics = async (spId, y, m) => {
    if (!spId || !y || !m) return;
    try {
      setMetricsLoading(true);
      const body = {
        salesPersonIds: [spId], // API expects array of _id
        year: Number(y),
        month: Number(m),
        database: db,
      };
      const res = await _Post(MonthMetrics, body);
      setMetricsRows(res?.rows || []);
    } catch (e) {
      console.error(e);
      setMetricsRows([]);
    } finally {
      setMetricsLoading(false);
    }
  };

  // Trigger metrics load when any dropdown changes (and all are selected)
  useEffect(() => {
    if (selectedSalesId && selectedYear && selectedMonth) {
      fetchMonthMetrics(selectedSalesId, selectedYear, selectedMonth);
    } else {
      setMetricsRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSalesId, selectedYear, selectedMonth]);

  // ----- columns -----
  const baseColumns = useMemo(
    () => [
      { key: "uid", header: "UID", get: (_, i) => i + 1 },
      {
        key: "actions",
        header: "ACTIONS",
        get: () => "",
        render: (r) => (
          <div className="tg-actions">
            <IoEye
              size={18}
              color="green"
              style={{ cursor: "pointer" }}
              title="Open hierarchy"
              onClick={() => fetchTargets(r?.userId?._id, r?.userId?.database)}
            />
          </div>
        ),
      },
      { key: "grandTotal", header: "TOTAL TARGET", get: (r) => r?.grandTotal },
      { key: "userCode", header: "USER CODE", get: (r) => r?.userId?.code },
      {
        key: "fullName",
        header: "FULL NAME",
        get: (r) =>
          `${toStr(r?.userId?.firstName)} ${toStr(r?.userId?.lastName)}`.trim(),
      },
      { key: "email", header: "EMAIL", get: (r) => r?.userId?.email },
      { key: "city", header: "CITY", get: (r) => r?.userId?.City },
      { key: "state", header: "STATE", get: (r) => r?.userId?.State },
      {
        key: "createdAt",
        header: "CREATED AT",
        get: (r) => fmtDate(r?.createdAt),
      },
      {
        key: "updatedAt",
        header: "UPDATED AT",
        get: (r) => fmtDate(r?.updatedAt),
      },
    ],
    [],
  );

  // column keys (persist to localStorage same key name “TargetList” to stay compatible)
  const [selectedKeys, setSelectedKeys] = useState([]);
  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    return m;
  }, [baseColumns]);

  useEffect(() => {
    const raw = localStorage.getItem("TargetList");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        if (typeof parsed[0] === "string")
          next = parsed.filter((k) => colMap.has(k));
      }
    } catch {}
    if (!next.length) next = baseColumns.map((c) => c.key);
    setSelectedKeys(next);
  }, [baseColumns, colMap]);

  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap],
  );

  // search
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const cols = visibleCols.filter((c) => c.key !== "actions");
    return rows.filter((r, i) =>
      cols.some((c) => toStr(c.get(r, i)).toLowerCase().includes(term)),
    );
  }, [rows, search, visibleCols]);

  // ----- data load -----
  const fetchTargets = async (userIdOverride) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");

      const inside = CheckPermission("Target Creation");
      if (inside && typeof inside === "object") {
        setPermissions((p) => ({ ...p, ...inside }));
      }
      setMasterShow(user?.rolename?.roleName === "MASTER");

      const qid = userIdOverride || id || user?._id;
      const res = await _Get(Heirarchy_Created_Target_List, qid);

      const list = Array.isArray(res?.Target) ? res.Target : [];

      // party branch: if first item contains partyId -> show TargetCreation
      const hasParty = !!list?.[0]?.partyId;
      if (hasParty) {
        setPartyData(list.slice().reverse());
        setPartyView(true);
        setRows([]);
        setSumTotal(0);
      } else {
        const totals = list.map((x) => toNum(x?.grandTotal) || 0);
        const sum = totals.reduce((a, b) => a + b, 0);
        setSumTotal(sum);
        setRows(list);
        setPartyView(false);
      }
    } catch (e) {
      console.error(e);
      setErrorMsg(
        e?.response?.data?.message || e?.message || "Unable to load targets.",
      );
      setRows([]);
      setPartyView(false);
      setSumTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTargets();
    loadSalesPeople();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ----- downloads (build CSV from visibleCols/filteredRows and pass to helpers) -----
  const buildCSV = () => {
    const cols = visibleCols;
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r, i) =>
      cols
        .map((c) => {
          const v = toStr(c.get(r, i)).replace(/"/g, '""');
          return `"${v}"`;
        })
        .join(","),
    );
    return [header, ...lines].join("\r\n");
  };

  const exportPDF = async () => {
    const csv = buildCSV();
    await exportDataToPDF(csv, "TargetList");
  };
  const exportCSV = () => {
    const csv = buildCSV();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "TargetList.csv";
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };
  const exportXLS = async () => {
    const csv = buildCSV();
    await convertDataCSVtoExcel(csv, "TargetList");
  };
  const exportXLSX = async () => {
    const csv = buildCSV();
    await exportDataToExcel(csv, "TargetList");
  };
  const exportXML = async () => {
    const csv = buildCSV();
    await convertDataCsvToXml(csv, "TargetList");
  };

  // ----- column chooser -----
  const openCols = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModalCols(true);
  };
  const closeCols = () => setModalCols(false);
  const handleCheckbox = (checked, key) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(key)) buf.push(key);
    } else {
      const i = buf.indexOf(key);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };
  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };
  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const applyColumns = () => {
    localStorage.setItem("TargetList", JSON.stringify(selectedKeys));
    closeCols();
  };

  // ----- SuperAdmin switch -----
  const handleParentSubmit = async () => {
    const a = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null",
    );
    if (!a) return;
    const [uid] = String(a).split(" ");
    await fetchTargets(uid);
  };
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  // ----- top-level loading -----
  if (loading && !rows.length && !partyView) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  // party branch: show TargetCreation (like your original)
  if (partyView) {
    return (
      <Row>
        <Col>
          <div className="d-flex justify-content-end p-1">
            <Button color="danger" onClick={() => setPartyView(false)}>
              Back
            </Button>
          </div>
        </Col>
        <Col sm="12">
          <TargetCreation ViewOneData={partyData} />
        </Col>
      </Row>
    );
  }

  const noData = !filteredRows.length;

  // selected salesperson mobile summary
  const selectedMobile = selectedSalesObj
    ? selectedSalesObj?.Mobile ||
      selectedSalesObj?.MobileNo ||
      selectedSalesObj?.mobile ||
      selectedSalesObj?.mobileNumber ||
      selectedSalesObj?.contact ||
      "N/A"
    : "";

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Toolbar (Dropdowns moved HERE) */}
      <Row className="tg-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="tg-card-light" style={{ padding: 8 }}>
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Target List{" "}
                <span className="count-pill">{filteredRows.length}</span>
              </div>

              <div style={{ flex: "1 1 60px" }} />

              {masterShow && (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              )}

              {/* Sales Person */}
              <div style={{ minWidth: 260, display: "grid", gap: 6 }}>
                <div
                  className="muted"
                  style={{ fontWeight: 700, fontSize: 12 }}
                >
                  Sales Person
                </div>
                <Input
                  type="select"
                  value={selectedSalesId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedSalesId(val);
                    setSelectedSalesObj(
                      salesPeople.find((s) => s._id === val) || null,
                    );
                  }}
                  style={{ background: "#fff", height: 32 }}
                >
                  <option value="">-- Select --</option>
                  {salesPeople.map((sp) => (
                    <option key={sp._id} value={sp._id}>
                      {sp.fullName} {sp.sId ? `${sp.sId}` : ""}
                    </option>
                  ))}
                </Input>

                <div className="chips">
                  {selectedSalesObj ? (
                    <div className="chip">
                      {selectedSalesObj.fullName} — {selectedMobile}
                    </div>
                  ) : (
                    <span className="muted" style={{ fontSize: 12 }}>
                      Select a sales person to view mobile
                    </span>
                  )}
                </div>
              </div>

              {/* Year */}
              <div style={{ minWidth: 140, display: "grid", gap: 6 }}>
                <div
                  className="muted"
                  style={{ fontWeight: 700, fontSize: 12 }}
                >
                  Year
                </div>
                <Input
                  type="select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  style={{ background: "#fff", height: 32 }}
                >
                  {YEARS.map((y) => (
                    <option value={y} key={y}>
                      {y}
                    </option>
                  ))}
                </Input>
              </div>

              {/* Month */}
              <div style={{ minWidth: 160, display: "grid", gap: 6 }}>
                <div
                  className="muted"
                  style={{ fontWeight: 700, fontSize: 12 }}
                >
                  Month
                </div>
                <Input
                  type="select"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  style={{ background: "#fff", height: 32 }}
                >
                  {MONTHS.map((m) => (
                    <option value={m.value} key={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Input>
              </div>

              {/* Search */}
              <div
                style={{
                  minWidth: 220,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff", height: 32 }}
                />
              </div>

              {permissions?.Create && (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    fontSize: 14,
                    textTransform: "uppercase",
                  }}
                  onClick={() => navigate("/app/targets/create")}
                >
                  <FaPlus size={12} /> Create Target
                </Button>
              )}

              {permissions?.View && (
                <Button
                  outline
                  color="primary"
                  onClick={openCols}
                  title="Filter Columns"
                  style={{ height: 32 }}
                >
                  <FaFilter size={14} />
                </Button>
              )}

              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div className="dl-menu">
                      <div className="dl-item" onClick={exportPDF}>
                        .PDF
                      </div>
                      <div className="dl-item" onClick={exportCSV}>
                        .CSV
                      </div>
                      <div className="dl-item" onClick={exportXLS}>
                        .XLS
                      </div>
                      <div className="dl-item" onClick={exportXLSX}>
                        .XLSX
                      </div>
                      <div className="dl-item" onClick={exportXML}>
                        .XML
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* TABLE AREA */}
      <Row>
        <Col sm="12">
          <Card className="tg-card" style={{ padding: 8 }}>
            {/* If a sales person is selected → show MonthMetrics table for selected year+month */}
            {selectedSalesId ? (
              metricsLoading ? (
                <div
                  style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
                >
                  <Spinner size="sm" color="primary" /> Loading metrics...
                </div>
              ) : metricsRows.length === 0 ? (
                <div
                  style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
                >
                  No data for the selected filters.
                </div>
              ) : (
                <div className="tg-table-wrap">
                  <table className="tg-table">
                    <thead>
                      <tr>
                        <th>PRODUCT NAME</th>
                        <th>TARGET QTY</th>
                        <th>SALE PRICE</th>
                        <th>PRODUCT TOTAL</th>

                        <th>CURRENT MONTH TARGET</th>
                        <th>LAST MONTH TARGET</th>
                        <th>YEARLY TARGET</th>

                        <th>CURRENT MONTH ACHIEVEMENT</th>
                        <th>LAST MONTH ACHIEVEMENT</th>
                        <th>YEARLY ACHIEVEMENT</th>

                        <th>CURRENT MONTH SHORTFALL</th>
                        <th>LAST MONTH SHORTFALL</th>
                        <th>YEARLY SHORTFALL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metricsRows.map((r, i) => (
                        <tr key={i}>
                          <td style={{ textAlign: "left" }}>
                            {toStr(r.productName)}
                          </td>
                          <td>{toNum(r.targetQty)}</td>
                          <td>{toNum(r.salePrice)}</td>
                          <td>{toNum(r.productTotal)}</td>

                          <td>{toNum(r.cmTarget)}</td>
                          <td>{toNum(r.lmTarget)}</td>
                          <td>{toNum(r.yTarget)}</td>

                          <td>{toNum(r.cmAch)}</td>
                          <td>{toNum(r.lmAch)}</td>
                          <td>{toNum(r.yAch)}</td>

                          <td>{toNum(r.cmShort)}</td>
                          <td>{toNum(r.lmShort)}</td>
                          <td>{toNum(r.yShort)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              // Original list table when no sales person selected
              <>
                {noData ? (
                  <div
                    style={{
                      padding: 24,
                      textAlign: "center",
                      color: "#6b7280",
                    }}
                  >
                    No targets found.
                  </div>
                ) : (
                  <div className="tg-table-wrap">
                    <table className="tg-table">
                      <thead>
                        <tr>
                          {visibleCols.map((c) => (
                            <th key={c.key}>{c.header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.map((r, i) => (
                          <tr key={r?._id || i}>
                            {visibleCols.map((c) => (
                              <td key={c.key}>
                                {typeof c.render === "function"
                                  ? c.render(r, i)
                                  : toStr(c.get(r, i))}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Footer Sum */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: "10px 6px 0 6px",
                  }}
                >
                  <strong>Total Target: {sumTotal}</strong>
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* Column Chooser */}
      <Modal
        isOpen={modalCols}
        toggle={closeCols}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeCols}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12">
              <h5>Available Columns</h5>
              <div className="pick-box">
                {baseColumns
                  .filter((c) => c.key !== "actions")
                  .map((c) => (
                    <div key={c.key} className="pick-item">
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <input
                          type="checkbox"
                          onChange={(e) =>
                            handleCheckbox(e.target.checked, c.key)
                          }
                        />
                        <span>{c.header}</span>
                      </label>
                    </div>
                  ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="pick-box" style={{ flex: 1 }}>
                  {selectedKeys.map((k, i) => {
                    const col = colMap.get(k);
                    if (!col) return null;
                    return (
                      <div
                        key={k}
                        className="pick-item"
                        style={{
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* View Assigned (if you still need it) */}
      <Modal
        isOpen={viewOpen}
        toggle={() => setViewOpen(false)}
        className="modal-dialog modal-xl"
        size="lg"
        backdrop={true}
        fullscreen={true}
      >
        <ModalHeader toggle={() => setViewOpen(false)}>
          View Details
        </ModalHeader>
        <ModalBody className="myproducttable">
          <TargetAssignedOne ViewData={viewRow} />
        </ModalBody>
      </Modal>
    </>
  );
}
